import {
  ChakraBox,
  TableFilterElement,
  UniversalSelect,
  FAIconName,
  Text,
} from '@equip.health/ui';
import { getMultiSelectedUniversalSelectOptions } from '~/lib/util/profile.util';
import React, { useState, useRef, useEffect } from 'react';
import { PatientDocumentsFilters } from '~/store/services/documents';
import { getFilterOptionLabel } from '~/lib/util/patientDocuments.util';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { DocumentCenterEvents } from '~/analytics/documentCenterEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

const FILTER_MIN_WIDTH = '150px';

interface FilterOptions {
  value: string;
  label: string;
  primaryText?: string;
}
type SelectableDocumentFilterKey =
  | 'documentTypes'
  | 'uploadedBy'
  | 'fileFormats';
interface DocumentsFilterSelectProps {
  id: string;
  title: string;
  filterKey: SelectableDocumentFilterKey;
  options: FilterOptions[];
  filters: PatientDocumentsFilters;
  activeFilters: Partial<Record<SelectableDocumentFilterKey, boolean>>;
  setActiveFilters: React.Dispatch<
    React.SetStateAction<Partial<Record<SelectableDocumentFilterKey, boolean>>>
  >;
  onFiltersChange?: (filters: PatientDocumentsFilters) => void;
  singleSelect?: boolean;
  usePrimaryTextAsLabel?: boolean;
}

const DocumentsFilterSelect = ({
  id,
  title,
  filterKey,
  options = [],
  filters,
  activeFilters,
  setActiveFilters,
  onFiltersChange,
  singleSelect = false,
  usePrimaryTextAsLabel = false,
}: DocumentsFilterSelectProps) => {
  const { trackEvent } = useAnalytics();

  const [draftFilterValues, setDraftFilterValues] =
    useState<PatientDocumentsFilters>({
      documentTypes: filters.documentTypes ?? [],
      uploadedBy: filters.uploadedBy ?? [],
      fileFormats: filters.fileFormats ?? [],
    });

  const draftFilterRef = useRef(draftFilterValues[filterKey] || []);
  const universalSelectRef = useRef<HTMLDivElement | null>(null);

  const handleMenuClose = () => {
    setActiveFilters((prev) => ({ ...prev, [filterKey]: false }));
  };

  useEffect(() => {
    setDraftFilterValues((prev) => ({
      ...prev,
      [filterKey]: filters[filterKey] || [],
    }));
    draftFilterRef.current = filters[filterKey] || [];
  }, [filters[filterKey]]);

  useEffect(() => {
    if (!activeFilters[filterKey]) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        universalSelectRef.current &&
        !universalSelectRef.current.contains(target)
      ) {
        handleMenuClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeFilters[filterKey], id]);

  const openUniversalSelectMenu = () => {
    let eventName;
    if (filterKey === 'uploadedBy') {
      eventName = DocumentCenterEvents.uploadedByFilterMenuClicked;
    } else if (filterKey === 'fileFormats') {
      eventName = DocumentCenterEvents.fileFormatFilterMenuClicked;
    } else {
      eventName = DocumentCenterEvents.documentTypeFilterMenuClicked;
    }
    trackEvent(eventName, {
      Source: AnalyticsSourceValues.DOCUMENT_CENTER,
      FilterKey: filterKey,
    });
    setActiveFilters((prev) => ({ ...prev, [filterKey]: true }));
  };

  const handleSelectChange = (selectedOptions: UniversalSelectOption[]) => {
    let eventName;
    if (filterKey === 'uploadedBy') {
      eventName = DocumentCenterEvents.uploadedByFilterMenuOptionClicked;
    } else if (filterKey === 'fileFormats') {
      eventName = DocumentCenterEvents.fileFormatFilterMenuOptionClicked;
    } else {
      eventName = DocumentCenterEvents.documentTypeFilterMenuOptionClicked;
    }
    trackEvent(eventName, {
      Source: AnalyticsSourceValues.DOCUMENT_CENTER,
      FilterKey: filterKey,
      SelectedOptionValues:
        selectedOptions?.map((option) => String(option.value)) || [],
      SelectedOptionLabels:
        selectedOptions?.map((option) =>
          usePrimaryTextAsLabel
            ? option.primaryText
            : getFilterOptionLabel(option.label),
        ) || [],
    });
    const mappedValues =
      selectedOptions?.map((option) => ({
        value: String(option.value),
        label: usePrimaryTextAsLabel
          ? option.primaryText
          : getFilterOptionLabel(option.label),
        primaryText: option.primaryText,
      })) || [];

    setDraftFilterValues((prev) => ({
      ...prev,
      [filterKey]: mappedValues,
    }));

    draftFilterRef.current = mappedValues;

    onFiltersChange?.({
      ...filters,
      [filterKey]: mappedValues,
    });
  };

  const getSelectedValues = (): string[] => {
    const currentValues = draftFilterValues[filterKey] || [];
    return currentValues.map((f) => String(f.value));
  };

  const hasSelectedFilters = (draftFilterValues[filterKey]?.length ?? 0) > 0;

  return (
    <ChakraBox minWidth={FILTER_MIN_WIDTH}>
      {hasSelectedFilters && !activeFilters[filterKey] ? (
        <TableFilterElement
          filter={{
            filterKey: filterKey as string,
            title,
            filters: draftFilterValues[filterKey],
          }}
          onRemove={openUniversalSelectMenu}
          rightIcon={FAIconName.chevronDownRegular}
        />
      ) : (
        <ChakraBox ref={universalSelectRef}>
          <UniversalSelect
            dropdownWidth={300}
            formatOptionLabel={(option, { context }) => {
              if (option.value === 'NO_TAGS') {
                return (
                  <Text
                    color="neutral.secondary"
                    id={`${id}__no-tags`}
                    variant="body"
                  >
                    {option.label}
                  </Text>
                );
              }

              if (!usePrimaryTextAsLabel) {
                return option.label;
              }

              return context === 'value' ? option.primaryText : option.label;
            }}
            id={id}
            isSingleSelect={singleSelect}
            menuIsOpen={activeFilters[filterKey]}
            onChange={handleSelectChange}
            onMenuClose={handleMenuClose}
            onMenuOpen={openUniversalSelectMenu}
            options={options}
            placeholder={title}
            shouldUseCustomDropdownWidth={title.toLowerCase() !== 'file format'}
            value={getMultiSelectedUniversalSelectOptions(
              getSelectedValues(),
              options,
            )}
            variant="slim"
            withCheckBox
          />
        </ChakraBox>
      )}
    </ChakraBox>
  );
};

export default DocumentsFilterSelect;
